<template>
  <RenderHTML
    ref="iconRef"
    tag-wrapper="span"
    :seotext="iconSvg"
    :class-wrapper="classes"
  />
</template>

<script setup lang="ts">
import { useGlobalStore } from '~/stores/global';
import RenderHTML from '~/components/ui/RenderHTML.vue';

const props = defineProps({
  // Название иконки, должно соответствовать названию файла в папках /assets/icon-lib/*
  // Комплект иконок можно посмотреть на странице uikit
  name: {
    type: String,
    required: true,
  },
  // размер иконки, параметр не работает, если указан responseSize
  size: {
    type: String,
    validator: (value: string) => ['16', '20', '24', '28'].includes(value),
  },
  // замена цвета fill в иконке
  fill: {
    type: String,
    default: '',
  },
  // замена цвета stroke в иконке
  stroke: {
    type: String,
    default: '',
  },
  // подсветка ховером через fill
  hoverFill: {
    type: String,
    default: '',
  },
  // подсветка ховером через stroke
  hoverStroke: {
    type: String,
    default: '',
  },
  // настройка адаптации размеров иконки, работает только при полном заполнении (массив 6 значений)
  // Валидны только размре 16, 20, 24 и 26
  // формат [es+, sm+, md+, lg+, xl+, xxl+]
  // например, иконка 16px до 1024 (мобила) и 20px 1024+:
  // [16, 16, 16, 20, 20, 20]
  responseSize: {
    type: Array,
    default: () => [],
  },
  display: {
    type: String,
    default: 'block',
  },
});
const iconRef = ref<HTMLElement>();
const iconSvg = ref('');
const globalStore = useGlobalStore();

const size = computed(() => {
  if (props?.size) return `${props.size}px`;
  return '16px';
});
const es = computed(() => {
  if (props.responseSize.length === 6) return `${props.responseSize[0]}px`;
  return '16px';
});
const sm = computed(() => {
  if (props.responseSize.length === 6) return `${props.responseSize[1]}px`;
  return '16px';
});
const md = computed(() => {
  if (props.responseSize.length === 6) return `${props.responseSize[2]}px`;
  return '16px';
});
const lg = computed(() => {
  if (props.responseSize.length === 6) return `${props.responseSize[3]}px`;
  return '16px';
});
const xl = computed(() => {
  if (props.responseSize.length === 6) return `${props.responseSize[4]}px`;
  return '16px';
});
const xxl = computed(() => {
  if (props.responseSize.length === 6) return `${props.responseSize[5]}px`;
  return '16px';
});
const classes = computed(() => {
  const list = ['nuxt-icon'];
  if (props.size && props.responseSize?.length !== 6)
    list.push(`nuxt-icon-size`);
  if (props.fill) list.push(`nuxt-icon-fill`);
  if (props.stroke) list.push(`nuxt-icon-stroke`);
  if (props.hoverFill) list.push(`nuxt-icon-hover-fill`);
  if (props.hoverStroke) list.push(`nuxt-icon-hover-stroke`);
  if (props.responseSize && props.responseSize.length === 6)
    list.push(`nuxt-icon-response`);
  return list.join(' ');
});
let iconsImport: { [key: string]: () => Promise<string> | any } = {};
if (!import.meta.env.SSR && globalStore.iconsArr) {
  iconsImport = globalStore.iconsArr;
} else {
  iconsImport = import.meta.glob('assets/icon-lib/**/**.svg', {
    as: 'raw',
    eager: false,
  });
  if (
    !import.meta.env.SSR &&
    globalStore &&
    globalStore.setIconsArr instanceof Function
  ) {
    globalStore.setIconsArr(iconsImport);
  }
}

const setIcon = async () => {
  try {
    const iconData: { [key: string]: string } = Object.keys(iconsImport).reduce(
      (data: { [key: string]: string }, icon) => {
        const fileName = icon
          .split('/')
          [icon.split('/').length - 1].replace('.svg', '');
        data[fileName] = icon;
        return data;
      },
      {}
    );
    const rawIcon = await iconsImport[iconData[props.name]]();
    if (rawIcon) iconSvg.value = rawIcon;
  } catch {
    console.error(
      `[nuxt-icons] Icon '${props?.name}' doesn't exist in 'assets/icon-lib'`
    );
  }
};
setIcon();

watch(
  () => props.name,
  () => {
    setIcon();
  }
);
</script>

<style lang="scss" scoped>
.nuxt-icon {
  display: v-bind(display);
  width: 16px;
  height: 16px;

  :deep(svg) {
    width: 16px;
    height: 16px;
  }
}

.nuxt-icon-size {
  width: v-bind(size);
  height: v-bind(size);

  :deep(svg) {
    width: v-bind(size);
    height: v-bind(size);
  }
}

.nuxt-icon-fill {
  :deep(svg) {
    fill: v-bind(fill);
  }

  :deep(path) {
    fill: v-bind(fill);
  }
}

.nuxt-icon-stroke {
  :deep(svg) {
    stroke: v-bind(stroke);
  }

  :deep(path) {
    stroke: v-bind(stroke);
  }
}

.nuxt-icon-hover-fill {
  &:hover {
    :deep(svg) {
      fill: v-bind(hoverFill);
    }

    :deep(path) {
      fill: v-bind(hoverFill);
    }
  }
}

.nuxt-icon-hover-stroke {
  &:hover {
    :deep(svg) {
      stroke: v-bind(hoverStroke);
    }

    :deep(path) {
      stroke: v-bind(hoverStroke);
    }
  }
}

.nuxt-icon-response {
  @include es-block {
    width: v-bind(es);
    height: v-bind(es);
    :deep(svg) {
      width: v-bind(es);
      height: v-bind(es);
    }
  }
  @include sm-block {
    width: v-bind(sm);
    height: v-bind(sm);
    :deep(svg) {
      width: v-bind(sm);
      height: v-bind(sm);
    }
  }
  @include md-block {
    width: v-bind(md);
    height: v-bind(md);
    :deep(svg) {
      width: v-bind(md);
      height: v-bind(md);
    }
  }
  @include lg-block {
    width: v-bind(lg);
    height: v-bind(lg);
    :deep(svg) {
      width: v-bind(lg);
      height: v-bind(lg);
    }
  }
  @include xl-block {
    width: v-bind(xl);
    height: v-bind(xl);
    :deep(svg) {
      width: v-bind(xl);
      height: v-bind(xl);
    }
  }
  @include xxl-block {
    width: v-bind(xxl);
    height: v-bind(xxl);
    :deep(svg) {
      width: v-bind(xxl);
      height: v-bind(xxl);
    }
  }
}
</style>
